import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  useBreakpointValue,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  MiniMap,
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from "react-zoom-pan-pinch";

interface PanZoomPinchWrapperProps {
  children: React.ReactNode;
  initialScale?: number;
  minScale?: number;
  maxScale?: number;
  limitToBounds?: boolean;
  centerOnInit?: boolean;
  centerZoomedOut?: boolean;
  wheel?: {
    step?: number;
  };
  smooth?: boolean;
  wrapperStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  showMiniMap?: boolean;
  showZoomControls?: boolean;
  enableMiniMapClickToCenter?: boolean;
  initialMiniMapPosition?: {
    top?: number;
  };
  transitionMiniMapPosition?: {
    top?: number;
  };
  seatLayoutRef?: React.RefObject<HTMLDivElement>;
  isDraggingRef?: React.MutableRefObject<boolean>;
}

export interface PanZoomPinchProps {
  miniMapWrapperProps?: BoxProps;
  zoomControlBoxProps?: BoxProps;
  zoomControlButtonProps?: ButtonProps;
}

const PanZoomPinchWrapper = ({
  children,
  initialScale = 0.8,
  minScale = 0.6,
  maxScale = 1.1,
  limitToBounds = false,
  centerOnInit = true,
  centerZoomedOut = false,
  wheel = { step: 0.05 },
  smooth = true,
  wrapperStyle,
  contentStyle,
  showMiniMap = true,
  showZoomControls = true,
  enableMiniMapClickToCenter = false,
  initialMiniMapPosition = { top: -1000 },
  transitionMiniMapPosition = { top: 16 },
  seatLayoutRef,
  isDraggingRef,
  miniMapWrapperProps,
  zoomControlBoxProps,
  zoomControlButtonProps,
}: PanZoomPinchWrapperProps & PanZoomPinchProps) => {
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [isKeyPressed, setIsKeyPressed] = useState<boolean>(false);
  const [miniMapPosition, setMiniMapPosition] = useState(
    initialMiniMapPosition,
  );

  // Session tracking for the 500ms show guard
  const lastTransformRef = useRef<number>(0);
  const transformSessionStartRef = useRef<number>(0);

  const miniMapWidth = useBreakpointValue({ base: 90, md: 220 }) ?? 220;
  const miniMapHeight = useBreakpointValue({ base: 58, md: 120 }) ?? 120;
  const miniMapPadding =
    useBreakpointValue({ base: "6px", md: "10px" }) ?? "10px";

  const handleMiniMapPosition = useCallback(
    debounce(() => {
      setMiniMapPosition(initialMiniMapPosition);
    }, 1500),
    [initialMiniMapPosition],
  );

  const handleBoundCheck = useCallback(
    debounce((e: ReactZoomPanPinchRef) => {
      if (!seatLayoutRef?.current || !parentRef.current) return;
      const content = seatLayoutRef.current;
      const parent = parentRef?.current;

      const contentWidth = content.clientWidth;
      const contentHeight = content.clientHeight;
      const parentWidth = parent.clientWidth;
      const parentHeight = parent.clientHeight;
      let { positionX, positionY, scale } = e.state;

      const maxX = parentWidth - parentWidth * 0.1;
      const minX = -(contentWidth - parentWidth * 0.1) * scale;
      const maxY = parentHeight - parentHeight * 0.1;
      const minY = -(contentHeight - parentHeight * 0.1) * scale;

      let newX = positionX;
      let newY = positionY;
      if (positionX > maxX) newX = maxX;
      if (positionX < minX) newX = minX;
      if (positionY > maxY) newY = maxY;
      if (positionY < minY) newY = minY;

      if (newX !== positionX || newY !== positionY) {
        e.setTransform(newX, newY, scale);
      }
    }, 100),
    [seatLayoutRef],
  );

  const onTransformed = (e: ReactZoomPanPinchRef) => {
    if (showMiniMap) {
      const now = Date.now();
      if (now - lastTransformRef.current > 300) {
        transformSessionStartRef.current = now;
      }
      lastTransformRef.current = now;
      if (
        miniMapPosition.top !== transitionMiniMapPosition.top &&
        now - transformSessionStartRef.current > 300
      ) {
        setMiniMapPosition(transitionMiniMapPosition);
        handleMiniMapPosition();
      }
    }
    handleBoundCheck(e);
  };

  const handlePanning = useCallback(
    debounce(() => {
      if (isDraggingRef) isDraggingRef.current = false;
    }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      handleBoundCheck.cancel?.();
      handlePanning.cancel?.();
    };
  }, [handleBoundCheck, handlePanning]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey || e.ctrlKey) {
        setIsKeyPressed(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.shiftKey && !e.ctrlKey) {
        setIsKeyPressed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  return (
    <Box
      ref={parentRef}
      style={{
        flex: 1,
        position: "relative",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <TransformWrapper
        ref={transformRef}
        initialScale={initialScale}
        minScale={minScale}
        maxScale={maxScale}
        limitToBounds={limitToBounds}
        centerOnInit={centerOnInit}
        wheel={wheel}
        panning={{ velocityDisabled: false, disabled: isKeyPressed }}
        pinch={{ disabled: false }}
        centerZoomedOut={centerZoomedOut}
        smooth={smooth}
        onTransformed={onTransformed}
        onPanning={() => {
          if (isDraggingRef) isDraggingRef.current = true;
          handlePanning();
        }}
      >
        <TransformComponent
          wrapperStyle={{
            width: "100%",
            height: "100%",
            ...wrapperStyle,
          }}
          contentStyle={{
            width: "fit-content",
            height: "fit-content",
            transformOrigin: "top left",
            ...contentStyle,
          }}
        >
          {children}
        </TransformComponent>
        {showMiniMap && (
          <Box
            {...miniMapWrapperProps}
            onClick={
              enableMiniMapClickToCenter
                ? () => transformRef.current?.centerView()
                : undefined
            }
            style={{
              position: "absolute",
              top: miniMapPosition.top,
              right: "16px",
              width: "fit-content",
              padding: miniMapPadding,
              height: "fit-content",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(0,0,0,0.35)",
              border: "1px solid rgba(0,0,0,0.08)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(4px)",
              transition: "top 0.4s ease-in-out",
              pointerEvents: enableMiniMapClickToCenter ? "auto" : "none",
              cursor: enableMiniMapClickToCenter ? "pointer" : "default",
              zIndex: 1,
              ...miniMapWrapperProps?.style,
            }}
          >
            <MiniMap width={miniMapWidth} height={miniMapHeight}>
              {children}
            </MiniMap>
          </Box>
        )}
      </TransformWrapper>
      {showZoomControls && (
        <Box
          {...zoomControlBoxProps}
          style={{
            position: "absolute",
            bottom: "180px",
            right: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            zIndex: 1,
            background: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
            borderRadius: "8px",
            padding: "8px",
            ...zoomControlBoxProps?.style,
          }}
        >
          <Button
            onClick={() => {
              transformRef.current?.zoomIn();
              handleMiniMapPosition();
            }}
            size={"xs"}
            {...zoomControlButtonProps}
          >
            +
          </Button>
          <Divider />
          <Button
            onClick={() => {
              transformRef.current?.zoomOut();
              handleMiniMapPosition();
            }}
            size={"xs"}
            {...zoomControlButtonProps}
          >
            −
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PanZoomPinchWrapper;
